import React from 'react';
import { useI18n } from '@shared/i18n/hooks/useI18n';
import type { EmployeeMessage } from '@shared/types';
import { RequestStatus } from '@features/requests/types';
import {
    getMessageStatusLabel,
    formatMessageDate,
} from '@features/requests/utils';
import styles from './ChatMessage.module.css';

/**
 * Props for ChatMessage component
 */
export interface ChatMessageProps {
    message: EmployeeMessage;
}

/**
 * Renders a single chat message with appropriate bubble or status badge styling
 */
export const ChatMessage = React.memo(function ChatMessage({
    message,
}: ChatMessageProps): React.ReactElement {
    const { t } = useI18n();

    const isOwn = message.status === 'COMMENTED';
    const isLeader = message.status === 'COMMENTED_BY_LEADER';
    const isApproved = message.status === RequestStatus.APPROVED;
    const isRejected = message.status === RequestStatus.REJECTED;
    const isInfo =
        message.status === RequestStatus.SUBMITTED ||
        message.status === RequestStatus.MODIFIED;

    const hasComment = message.message && message.message.trim() !== '';

    const getBubbleStyle = (): string => {
        if (isOwn) return styles.bubbleOwn;
        if (isLeader) return styles.bubbleLeader;
        if (isApproved) return styles.bubbleApproved;
        if (isRejected) return styles.bubbleRejected;
        if (isInfo) return styles.bubbleInfo;
        return styles.bubbleSystem;
    };

    const getRowStyle = (): string => {
        if (isOwn) return styles.own;
        if (isLeader) return styles.leader;
        return styles.system;
    };

    const getAuthorText = (): string => {
        if (isOwn) return 'Du';
        if (isLeader) return 'Leder';
        if (isApproved) return 'Godkjent';
        if (isRejected) return 'Avslått';
        if (message.status === RequestStatus.MODIFIED)
            return t('requests.status.modified');
        return getMessageStatusLabel(message.status);
    };

    return (
        <div
            className={`${styles.row} ${getRowStyle()}`}
            data-testid="chat-message"
        >
            <div className={`${styles.bubble} ${getBubbleStyle()}`}>
                <span className={styles.author}>{getAuthorText()}</span>
                {hasComment && <p className={styles.text}>{message.message}</p>}
                {message.registered && (
                    <span className={styles.date}>
                        {formatMessageDate(message.registered)}
                    </span>
                )}
            </div>
        </div>
    );
});
